'use client';

import { useState, useCallback } from 'react';
import Link from 'next/link';
import { LocaleLink } from '@/core/i18n';
import { ArrowRight, ChevronDown, CheckCircle2, Quote, MapPin } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import type { LucideIcon } from 'lucide-react';
import { usePageContent } from '@/core/i18n/content';
import { TEMPLATE_CHROME } from '@/core/i18n/content/template-chrome.content';
import type { TemplateChromeContent } from '@/core/i18n/content/template-chrome.types';
import { INDUSTRY_TRANSLATIONS, type IndustryText } from '@/core/i18n/content/industry.content';
import { useLocaleContext } from '@/core/i18n/locale.context';
import { useLocaleStore } from '@/core/i18n/locale.store';
import { INDUSTRY_CONFIGS } from './seo-industry.data';

/* -------------------------------------------------------------------------- */
/*  Types                                                                     */
/* -------------------------------------------------------------------------- */

export interface IndustryLandingConfig {
  slug: string;
  propertyType: string;
  heroTitle: string;
  heroSubtitle: string;
  painPoints: { icon: LucideIcon; title: string; description: string }[];
  solutionFeatures: { icon: LucideIcon; title: string; description: string; highlight?: string }[];
  caseStudy: { propertyName: string; location: string; challenge: string; solution: string; results: string[] };
  stats: { value: string; label: string }[];
  testimonial: { quote: string; name: string; role: string; hotel: string; initials: string };
  faqItems: { question: string; answer: string }[];
  relatedIndustries: { href: string; label: string; description: string }[];
}

/* -------------------------------------------------------------------------- */
/*  Section -- Hero                                                           */
/* -------------------------------------------------------------------------- */

function IndustryHero({ config, t }: { config: IndustryLandingConfig; t: TemplateChromeContent }) {
  const pfx = `industry-${config.slug}`;
  return (
    <section id={`${pfx}-hero`} className="relative w-full overflow-hidden bg-hms-bg">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 30%, rgba(200,145,58,0.08) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-4xl px-4 pb-16 pt-24 text-center sm:px-6 sm:pt-32 lg:px-8 lg:pt-40">
        <h1
          id={`${pfx}-title`}
          className={cn(
            'font-poppins text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {config.heroTitle}
        </h1>
        <p id={`${pfx}-subtitle`} className="mx-auto mt-6 max-w-2xl text-base text-hms-textSub sm:text-lg">
          {config.heroSubtitle}
        </p>
        <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <LocaleLink
            id={`${pfx}-cta-trial`}
            href="/register-hotel"
            className={cn(
              'inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-bg shadow-lg shadow-hms-gold/20',
              'transition-all duration-200 hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]',
              'w-full sm:w-auto justify-center',
            )}
          >
            {t.cta.trialBtn}
            <ArrowRight className="h-4 w-4" />
          </LocaleLink>
          <LocaleLink
            id={`${pfx}-cta-demo`}
            href="/contact"
            className={cn(
              'inline-flex items-center justify-center rounded-lg border border-hms-border px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-text',
              'transition-all duration-200 hover:border-hms-gold/40 hover:text-hms-gold',
              'w-full sm:w-auto',
            )}
          >
            {t.cta.bookDemo}
          </LocaleLink>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section -- Pain Points                                                    */
/* -------------------------------------------------------------------------- */

function IndustryPainPoints({ config, t }: { config: IndustryLandingConfig; t: TemplateChromeContent }) {
  const pfx = `industry-${config.slug}`;
  return (
    <section id={`${pfx}-pain-points`} className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
      <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <h2
          id={`${pfx}-pain-points-title`}
          className="text-center text-2xl font-bold tracking-tight text-hms-text sm:text-3xl"
        >
          {t.seoTemplate.challengesFace.replace('{0}', config.propertyType)}
        </h2>
        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2">
          {config.painPoints.map((point, idx) => {
            const Icon = point.icon;
            return (
              <div
                key={idx}
                id={`${pfx}-pain-${idx}`}
                className={cn(
                  'group flex items-start gap-4 rounded-xl border border-hms-border bg-hms-card p-5',
                  'transition-all duration-300 hover:border-hms-red/20',
                )}
              >
                <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-hms-red/10">
                  <Icon className="h-4.5 w-4.5 text-hms-red" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-bold text-hms-text">{point.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-hms-textSub">{point.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section -- Solution Features                                              */
/* -------------------------------------------------------------------------- */

function IndustrySolutions({ config, t }: { config: IndustryLandingConfig; t: TemplateChromeContent }) {
  const pfx = `industry-${config.slug}`;
  return (
    <section id={`${pfx}-solutions`} className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 40%, rgba(200,145,58,0.04) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h2
          id={`${pfx}-solutions-title`}
          className={cn(
            'text-center text-3xl font-extrabold tracking-tight sm:text-4xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {t.seoTemplate.howHelps.replace('{0}', config.propertyType)}
        </h2>
        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {config.solutionFeatures.map((feat, idx) => {
            const Icon = feat.icon;
            return (
              <div
                key={feat.title}
                id={`${pfx}-solution-${idx}`}
                className={cn(
                  'group rounded-xl border border-hms-border bg-hms-card p-6',
                  'transition-all duration-300 hover:border-hms-gold/20 hover:-translate-y-1 hover:shadow-lg hover:shadow-black/20',
                )}
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-hms-gold/10 transition-colors group-hover:bg-hms-gold/15">
                  <Icon className="h-5 w-5 text-hms-gold" />
                </div>
                <h3 className="mb-2 text-base font-bold tracking-tight text-hms-text">{feat.title}</h3>
                <p className="text-sm leading-relaxed text-hms-textSub">{feat.description}</p>
                {feat.highlight && (
                  <span className="mt-3 inline-block rounded-full bg-hms-gold/10 px-3 py-1 text-xs font-semibold text-hms-gold">
                    {feat.highlight}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section -- Case Study                                                     */
/* -------------------------------------------------------------------------- */

function IndustryCaseStudy({ config, t }: { config: IndustryLandingConfig; t: TemplateChromeContent }) {
  const pfx = `industry-${config.slug}`;
  const cs = config.caseStudy;
  return (
    <section id={`${pfx}-case-study`} className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
      <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <h2
          id={`${pfx}-case-study-title`}
          className={cn(
            'text-center text-3xl font-extrabold tracking-tight sm:text-4xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {t.seoTemplate.realResults}
        </h2>
        <div
          id={`${pfx}-case-study-card`}
          className="mt-12 rounded-xl border border-hms-border bg-hms-card p-6 sm:p-8"
        >
          {/* Header */}
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
            <h3 className="text-lg font-bold text-hms-text">{cs.propertyName}</h3>
            <div className="flex items-center gap-1 text-xs text-hms-textDim">
              <MapPin className="h-3 w-3" />
              <span>{cs.location}</span>
            </div>
          </div>

          {/* Challenge & Solution */}
          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-hms-red">{t.seoTemplate.challenge}</p>
              <p className="text-sm leading-relaxed text-hms-textSub">{cs.challenge}</p>
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-hms-teal">{t.seoTemplate.solution}</p>
              <p className="text-sm leading-relaxed text-hms-textSub">{cs.solution}</p>
            </div>
          </div>

          {/* Results */}
          <div className="mt-6 border-t border-hms-border pt-6">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-hms-green">{t.seoTemplate.results}</p>
            <ul className="space-y-2">
              {cs.results.map((result, idx) => (
                <li key={idx} id={`${pfx}-case-result-${idx}`} className="flex items-start gap-2.5">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-hms-green" />
                  <span className="text-sm leading-relaxed text-hms-textSub">{result}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section -- Stats                                                          */
/* -------------------------------------------------------------------------- */

function IndustryStats({ config }: { config: IndustryLandingConfig }) {
  const pfx = `industry-${config.slug}`;
  return (
    <section id={`${pfx}-stats`} className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 gap-5 lg:grid-cols-4">
          {config.stats.map((stat, idx) => (
            <div
              key={idx}
              id={`${pfx}-stat-${idx}`}
              className="rounded-xl border border-hms-border bg-hms-card p-6 text-center"
            >
              <p
                className={cn(
                  'text-3xl font-extrabold tracking-tight sm:text-4xl',
                  'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
                )}
              >
                {stat.value}
              </p>
              <p className="mt-2 text-sm font-semibold text-hms-text">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section -- Testimonial                                                    */
/* -------------------------------------------------------------------------- */

function IndustryTestimonial({ config }: { config: IndustryLandingConfig }) {
  const pfx = `industry-${config.slug}`;
  const t = config.testimonial;
  return (
    <section id={`${pfx}-testimonial`} className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
      <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <div id={`${pfx}-testimonial-card`} className="rounded-xl border border-hms-border bg-hms-card p-8 text-center">
          <Quote className="mx-auto mb-4 h-8 w-8 text-hms-gold/20" />
          <p className="text-base italic leading-relaxed text-hms-textSub sm:text-lg">&ldquo;{t.quote}&rdquo;</p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-hms-gold/20">
              <span className="text-xs font-bold text-hms-gold">{t.initials}</span>
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-hms-text">{t.name}</p>
              <p className="text-xs text-hms-textDim">{t.role}, {t.hotel}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section -- FAQ                                                            */
/* -------------------------------------------------------------------------- */

function IndustryFAQ({ config, t }: { config: IndustryLandingConfig; t: TemplateChromeContent }) {
  const pfx = `industry-${config.slug}`;
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const toggle = useCallback((i: number) => setOpenIndex((p) => (p === i ? null : i)), []);

  return (
    <section id={`${pfx}-faq`} className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <h2
          id={`${pfx}-faq-title`}
          className={cn(
            'text-center text-3xl font-extrabold tracking-tight sm:text-4xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {t.common.faq}
        </h2>
        <div className="mt-12 space-y-3">
          {config.faqItems.map((item, idx) => (
            <div
              key={idx}
              id={`${pfx}-faq-item-${idx}`}
              className={cn(
                'rounded-xl border transition-colors',
                openIndex === idx
                  ? 'border-hms-gold/30 bg-hms-card'
                  : 'border-hms-border bg-hms-card/50 hover:border-hms-gold/15',
              )}
            >
              <button
                id={`${pfx}-faq-toggle-${idx}`}
                type="button"
                onClick={() => toggle(idx)}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                aria-expanded={openIndex === idx}
              >
                <span
                  className={cn(
                    'text-sm font-semibold leading-snug',
                    openIndex === idx ? 'text-hms-gold' : 'text-hms-text',
                  )}
                >
                  {item.question}
                </span>
                <ChevronDown
                  className={cn(
                    'h-4 w-4 shrink-0 transition-transform duration-200',
                    openIndex === idx ? 'rotate-180 text-hms-gold' : 'text-hms-textDim',
                  )}
                />
              </button>
              {openIndex === idx && (
                <div className="px-5 pb-5">
                  <p className="text-sm leading-relaxed text-hms-textSub">{item.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section -- Related Industries                                             */
/* -------------------------------------------------------------------------- */

function IndustryRelated({ config, t }: { config: IndustryLandingConfig; t: TemplateChromeContent }) {
  const pfx = `industry-${config.slug}`;
  if (config.relatedIndustries.length === 0) return null;
  return (
    <section id={`${pfx}-related`} className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h2 className="text-center text-2xl font-bold tracking-tight text-hms-text sm:text-3xl">
          {t.common.alsoBuiltFor}
        </h2>
        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {config.relatedIndustries.map((ind, idx) => (
            <LocaleLink
              key={ind.href}
              id={`${pfx}-related-${idx}`}
              href={ind.href}
              className={cn(
                'group flex items-start gap-3 rounded-xl border border-hms-border bg-hms-card p-5',
                'transition-all duration-200 hover:border-hms-gold/20 hover:-translate-y-0.5',
              )}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-hms-text transition-colors group-hover:text-hms-gold">
                  {ind.label}
                </p>
                <p className="mt-1 text-xs leading-relaxed text-hms-textDim">{ind.description}</p>
              </div>
              <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-hms-textDim transition-colors group-hover:text-hms-gold" />
            </LocaleLink>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section -- CTA                                                            */
/* -------------------------------------------------------------------------- */

function IndustryCTA({ config, t }: { config: IndustryLandingConfig; t: TemplateChromeContent }) {
  const pfx = `industry-${config.slug}`;
  return (
    <section id={`${pfx}-cta`} className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 50%, rgba(200,145,58,0.06) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
        <h2
          id={`${pfx}-cta-title`}
          className={cn(
            'text-3xl font-extrabold tracking-tight sm:text-4xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {t.common.readyToStart}
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-base text-hms-textSub sm:text-lg">
          {t.seoTemplate.joinHundredsProperty.replace('{0}', config.propertyType.toLowerCase())}
        </p>
        <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <LocaleLink
            id={`${pfx}-cta-final-trial`}
            href="/register-hotel"
            className={cn(
              'inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-bg shadow-lg shadow-hms-gold/20',
              'transition-all duration-200 hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]',
              'w-full sm:w-auto justify-center',
            )}
          >
            {t.cta.trialBtn}
            <ArrowRight className="h-4 w-4" />
          </LocaleLink>
          <LocaleLink
            id={`${pfx}-cta-final-contact`}
            href="/contact"
            className={cn(
              'inline-flex items-center justify-center rounded-lg border border-hms-border px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-text',
              'transition-all duration-200 hover:border-hms-gold/40 hover:text-hms-gold',
              'w-full sm:w-auto',
            )}
          >
            {t.cta.contactBtn}
          </LocaleLink>
        </div>
        <p className="mt-5 text-xs text-hms-textDim">
          {t.common.noCreditCard}
        </p>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Translation merge — overlay locale text onto English config               */
/* -------------------------------------------------------------------------- */

function mergeIndustryConfig(cfg: IndustryLandingConfig, txt: IndustryText | undefined): IndustryLandingConfig {
  if (!txt) return cfg;
  return {
    ...cfg,
    propertyType: txt.propertyType,
    heroTitle: txt.heroTitle,
    heroSubtitle: txt.heroSubtitle,
    painPoints: cfg.painPoints.map((p, i) => ({
      ...p, // keeps icon
      title: txt.painPoints[i]?.title ?? p.title,
      description: txt.painPoints[i]?.description ?? p.description,
    })),
    solutionFeatures: cfg.solutionFeatures.map((f, i) => ({
      ...f, // keeps icon
      title: txt.solutionFeatures[i]?.title ?? f.title,
      description: txt.solutionFeatures[i]?.description ?? f.description,
      highlight: txt.solutionFeatures[i]?.highlight ?? f.highlight,
    })),
    caseStudy: {
      ...cfg.caseStudy, // keeps propertyName, location
      challenge: txt.caseStudy.challenge,
      solution: txt.caseStudy.solution,
      results: txt.caseStudy.results,
    },
    stats: cfg.stats.map((s, i) => ({
      ...s, // keeps value
      label: txt.statsLabels[i] ?? s.label,
    })),
    testimonial: {
      ...cfg.testimonial, // keeps name, initials
      quote: txt.testimonial.quote,
      role: txt.testimonial.role,
      hotel: txt.testimonial.hotel,
    },
    faqItems: txt.faqItems.map((f, i) => ({
      question: f.question,
      answer: f.answer,
    })),
    relatedIndustries: cfg.relatedIndustries.map((r, i) => ({
      ...r, // keeps href
      label: txt.relatedIndustries[i]?.label ?? r.label,
      description: txt.relatedIndustries[i]?.description ?? r.description,
    })),
  };
}

/* -------------------------------------------------------------------------- */
/*  Main Component                                                            */
/* -------------------------------------------------------------------------- */

export function IndustryLanding({ slug }: { slug: string }) {
  const baseConfig = INDUSTRY_CONFIGS[slug];
  const t = usePageContent(TEMPLATE_CHROME);

  const ctxLocale = useLocaleContext();
  const storeLocale = useLocaleStore((s) => s.locale);
  const locale = ctxLocale ?? storeLocale;
  const localeTexts = locale !== 'en' ? INDUSTRY_TRANSLATIONS[locale] : undefined;
  const translated = localeTexts?.[slug];
  const config = mergeIndustryConfig(baseConfig, translated);

  return (
    <main id={`industry-${config.slug}-page`}>
      <IndustryHero config={config} t={t} />
      <IndustryPainPoints config={config} t={t} />
      <IndustrySolutions config={config} t={t} />
      <IndustryCaseStudy config={config} t={t} />
      <IndustryStats config={config} />
      <IndustryTestimonial config={config} />
      <IndustryFAQ config={config} t={t} />
      <IndustryRelated config={config} t={t} />
      <IndustryCTA config={config} t={t} />
    </main>
  );
}
